import {getEnvironment} from "../lib/get-environment";

/**
 * Identity Provider client config (per-environment).
 *
 * Provider-agnostic shape — Auth0 today, Zitadel migration is a config swap
 * plus an SDK swap inside `useIdpClient.ts`. The `provider` field disambiguates
 * provider-specific behavior at the SDK boundary.
 *
 * @module client/Auth/idp-config
 */

export type IdpProvider = 'auth0' | 'zitadel';

export interface IdpConfig {
    provider: IdpProvider;
    audience: string;
    domain: string;
    clientId: string;
    callbackUrl: string;
    afterLogout: string;
}

const IDP_CONFIG_DEV: IdpConfig = {
    provider: 'auth0',
    audience: 'https://api.outlawpractice.com',
    domain: 'auth-dev.outlawpractice.com',
    clientId: 'ifJPp6HM7WuvhEYuoUSoPb62t16Y7hdF',
    callbackUrl: `${window.location.protocol}//${window.location.host}/callback`,
    afterLogout: `${window.location.protocol}//${window.location.host}/`,
};

const IDP_CONFIG_TEST: IdpConfig = {
    provider: 'auth0',
    audience: 'https://api.outlawpractice.com',
    domain: 'auth-test.outlawpractice.com',
    clientId: 'CUIGoH4kOIShUfbifW7AKaDPHaUvkr8B',
    callbackUrl: 'https://app.outlawpractice.dev/callback',
    afterLogout: 'https://app.outlawpractice.dev/',
};

const IDP_CONFIG_DEMO: IdpConfig = {
    provider: 'auth0',
    audience: 'https://api.outlawpractice.com',
    domain: 'auth-test.outlawpractice.com',
    clientId: 'CUIGoH4kOIShUfbifW7AKaDPHaUvkr8B',
    callbackUrl: 'https://app-demo.outlawpractice.com/callback',
    afterLogout: 'https://app-demo.outlawpractice.com/',
};

const IDP_CONFIG_BETA: IdpConfig = {
    provider: 'auth0',
    audience: 'https://api.outlawpractice.com',
    domain: 'auth.outlawpractice.com',
    clientId: 'cGMEchitYrYfM3uRMgBGvFIYtoOSc9ks',
    callbackUrl: `${window.location.protocol}//${window.location.host}/callback`,
    afterLogout: `${window.location.protocol}//${window.location.host}/`,
};

const IDP_CONFIG_PROD: IdpConfig = {
    provider: 'auth0',
    audience: 'https://api.outlawpractice.com',
    domain: 'auth.outlawpractice.com',
    clientId: 'cGMEchitYrYfM3uRMgBGvFIYtoOSc9ks',
    callbackUrl: 'https://app.outlawpractice.com/callback',
    afterLogout: 'https://app.outlawpractice.com/',
};

let IDP_CONFIG: IdpConfig;

switch (getEnvironment()) {
    case 'P':
        IDP_CONFIG = IDP_CONFIG_PROD;
        break;
    case 'B':
        IDP_CONFIG = IDP_CONFIG_BETA;
        break;
    case 'DM':
        IDP_CONFIG = IDP_CONFIG_DEMO;
        break;
    case 'T':
        IDP_CONFIG = IDP_CONFIG_TEST;
        break;
    default:
        IDP_CONFIG = IDP_CONFIG_DEV;
}

export default IDP_CONFIG;

// Backward-compatible aliases (to be removed once all importers move to IdpConfig/IDP_CONFIG).
export type AuthConfig = IdpConfig;
